import React, { useState, useEffect, useRef, useCallback } from "react";
import { Power, Cpu, Activity, Radio, ShieldCheck, FileText, Clock, MessageSquareX, Wifi, Zap, Mic, Loader2 } from "lucide-react";
import { nexusChannel, nexusIdle, nexusTalk, audioUrlForKey, listDocuments, getReminders, waDeleted } from "../lib/api";
import NexusFace3D from "./NexusFace3D";
import { WallTicker } from "./NexusWall";
import { TvTickerBar } from "./tv/TvTickerBar";
import { NexusScenes } from "./tv/NexusScenes";
import { NexusBoot } from "./tv/NexusBoot";
import { LiveClock } from "./LiveClock";
import { eyeGet, tvControlGet } from "../lib/api";
import { TvEyeAlert } from "./tv/TvEyeAlert";
import { TvChannelFeed } from "./tv/ChannelVideos";
import { toast } from "sonner";

const MOOD_COLOR = { danger: "#EF4444", good: "#10B981", neutral: null };
const SPECTRUM = ["#00F0FF", "#7C3AED", "#EC4899", "#F59E0B", "#22D3EE", "#10B981", "#3B82F6", "#F43F5E", "#A855F7", "#14B8A6"];

const STATES = {
  idle: { color: "#00F0FF", label: "SAY “HEY NEXUS”", sub: "STANDBY" },
  listening: { color: "#00D8F6", label: "LISTENING", sub: "AUDIO INPUT ACTIVE" },
  thinking: { color: "#FFB703", label: "PROCESSING", sub: "DATA MATRIX QUERY" },
  speaking: { color: "#00F0FF", label: "NEXUS RESPONDING", sub: "VOICE SYNTHESIS" },
};

/* Background particle starfield drifting toward viewer */
function Particles({ color }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, W, H, stars = [];
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    resize();
    const N = Math.min(150, Math.floor((W * H) / 14000));
    stars = Array.from({ length: N }, () => ({ x: (Math.random() - 0.5) * W, y: (Math.random() - 0.5) * H, z: Math.random() * W }));
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.save(); ctx.translate(W / 2, H / 2);
      for (const s of stars) {
        s.z -= 1.4; if (s.z <= 1) { s.z = W; s.x = (Math.random() - 0.5) * W; s.y = (Math.random() - 0.5) * H; }
        const k = 128 / s.z; const px = s.x * k, py = s.y * k;
        const r = Math.max(0.2, (1 - s.z / W) * 1.8);
        ctx.beginPath(); ctx.arc(px, py, r, 0, 6.28);
        ctx.fillStyle = color + Math.floor((1 - s.z / W) * 200 + 30).toString(16).padStart(2, "0");
        ctx.fill();
      }
      ctx.restore(); raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [color]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" data-testid="nexus-particles" />;
}

function Ring({ size, dash, cls, color, opacity = 0.5, width = 1 }) {
  const r = size / 2 - 4;
  return (
    <svg width={size} height={size} className={`absolute ${cls}`} style={{ left: `calc(50% - ${size / 2}px)`, top: `calc(50% - ${size / 2}px)` }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeOpacity={opacity} strokeWidth={width} strokeDasharray={dash} />
    </svg>
  );
}

/* Radial tick marks around core */
function Ticks({ size, color }) {
  const ticks = Array.from({ length: 60 });
  return (
    <svg width={size} height={size} className="absolute nx-spin-slow" style={{ left: `calc(50% - ${size / 2}px)`, top: `calc(50% - ${size / 2}px)` }}>
      {ticks.map((_, i) => {
        const a = (i / 60) * 2 * Math.PI; const big = i % 5 === 0;
        const r1 = size / 2 - 6, r2 = size / 2 - (big ? 18 : 11);
        const cx = size / 2, cy = size / 2;
        return <line key={i} x1={cx + r1 * Math.cos(a)} y1={cy + r1 * Math.sin(a)} x2={cx + r2 * Math.cos(a)} y2={cy + r2 * Math.sin(a)}
          stroke={color} strokeOpacity={big ? 0.7 : 0.3} strokeWidth={big ? 2 : 1} />;
      })}
    </svg>
  );
}

export default function NexusTV({ session, screen }) {
  const [sess, setSess] = useState(null);
  const [started, setStarted] = useState(false);
  const [booting, setBooting] = useState(false);
  const [status, setStatus] = useState("idle");
  const [userText, setUserText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [ctxData, setCtxData] = useState({ docs: 0, deadlines: 0, wa: 0 });
  const [clock, setClock] = useState("");
  const [preview, setPreview] = useState(null); // manual state override for visuals
  const [recording, setRecording] = useState(false);
  const [wake, setWake] = useState(false);
  const [mood, setMood] = useState("neutral");
  const [turnId, setTurnId] = useState(0);
  const [variant, setVariant] = useState(() => {
    try { return localStorage.getItem("nexus_core_variant") || "neuron"; } catch (e) { return "face"; }
  });
  const setCoreVariant = (v) => { setVariant(v); try { localStorage.setItem("nexus_core_variant", v); } catch (e) {} };
  const [layout, setLayout] = useState(() => { try { return localStorage.getItem("nexus_tv_layout") || "wall"; } catch (e) { return "wall"; } });
  const layoutRef = useRef(layout); layoutRef.current = layout;
  const variantRef = useRef(variant); variantRef.current = variant;
  useEffect(() => {
    const t = setInterval(() => tvControlGet(session.user_id).then((c) => {
      if (c.layout && c.layout !== layoutRef.current) { setLayout(c.layout); try { localStorage.setItem("nexus_tv_layout", c.layout); } catch (e) {} }
      if (c.variant && c.variant !== variantRef.current) setCoreVariant(c.variant);
    }).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [session.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [faceMood, setFaceMood] = useState("neutral");
  const [eyeOn, setEyeOn] = useState(false);
  const [eyeAlarm, setEyeAlarm] = useState(false);
  const [eyeWho, setEyeWho] = useState([]);
  const [eyeBanner, setEyeBanner] = useState(null);
  const lastEventRef = useRef(null);
  const gazeRef = useRef(null);
  const onPnl = useCallback((v) => setFaceMood(v > 5 ? "good" : v < -5 ? "danger" : "neutral"), []);
  const wall = layout === "wall";

  const audioRef = useRef(null);
  const lastTurn = useRef(0);
  const coreRef = useRef(null);
  const barsWrap = useRef(null);
  const radialWrap = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const levelRef = useRef(0);
  const mediaRef = useRef(null);
  const recogRef = useRef(null);
  const busyRef = useRef(false);

  const effState = preview || status;
  const s = STATES[effState];
  const neutralSpectrum = SPECTRUM[turnId % SPECTRUM.length];
  const color = eyeAlarm ? "#EF4444"
    : mood === "danger" ? "#EF4444"
    : (mood === "good" || eyeWho.length) ? "#10B981"
    : (effState === "thinking" || effState === "listening") ? s.color
    : neutralSpectrum;

  useEffect(() => {
    (async () => {
      try { const r = await nexusChannel(session.user_id); setSess({ session_id: r.session_id }); }
      catch (e) { toast.error("Could not start Nexus display"); }
    })();
  }, [session.user_id]);

  // live context radar (real data)
  useEffect(() => {
    (async () => {
      try {
        const [docs, rem, wa] = await Promise.all([
          listDocuments(session.user_id).catch(() => []),
          getReminders(session.user_id).catch(() => []),
          waDeleted(session.user_id).catch(() => []),
        ]);
        setCtxData({ docs: (docs || []).length, deadlines: (rem || []).length, wa: (wa || []).length });
      } catch (e) {}
    })();
  }, [session.user_id]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })), 1000);
    return () => clearInterval(t);
  }, []);

  // Nexus's eye: laptop camera sends face position + recognised names; red on strangers, green for family/owner
  useEffect(() => {
    if (!started) return;
    let stop = false;
    const poll = async () => {
      try {
        const d = await eyeGet(session.user_id);
        setEyeOn(!!d.online);
        setEyeAlarm(!!d.alarm);
        setEyeWho(d.online && d.present ? d.who || [] : []);
        if (d.online) gazeRef.current = { ...d, at: Date.now() };
        const ev = d.event;
        if (ev && ev.id !== lastEventRef.current) {
          const fresh = lastEventRef.current !== null && Date.now() / 1000 - ev.ts < 30;
          lastEventRef.current = ev.id;
          if (fresh) {
            setEyeBanner(ev);
            setTimeout(() => setEyeBanner(null), ev.kind === "intruder" ? 20000 : ev.kind === "business" ? 35000 : 10000);
            if (ev.kind === "business") window.dispatchEvent(new CustomEvent("nexus-scene", { detail: "business" }));
            const a = audioRef.current, busy = a && !a.paused && !a.ended && Date.now() - (a._eyeAt || 0) < 90000;
            if (ev.audio_key && a && !(busy && !["intruder", "health"].includes(ev.kind))) { a.src = audioUrlForKey(ev.audio_key); a._eyeAt = Date.now(); a.play().catch(() => {}); }
          }
        }
      } catch (e) {}
      if (!stop) setTimeout(poll, 500);
    };
    poll();
    return () => { stop = true; };
  }, [started, session.user_id]);

  // poll shared channel (no pairing)
  useEffect(() => {
    if (!sess?.session_id || !started) return;
    let stop = false;
    const poll = async () => {
      try {
        const st = await nexusChannel(session.user_id);
        setStatus(st.status || "idle");
        setUserText(st.user_text || "");
        setReplyText(st.reply_text || "");
        if (st.mood) setMood(st.mood);
        if (st.turn_id) setTurnId(st.turn_id);
        if (st.status === "speaking" && st.turn_id > lastTurn.current && st.audio_key) {
          lastTurn.current = st.turn_id;
          if (audioRef.current) { audioRef.current.src = audioUrlForKey(st.audio_key); audioRef.current.play().catch(() => {}); }
        }
      } catch (e) {}
      if (!stop) setTimeout(poll, 1100);
    };
    poll();
    return () => { stop = true; };
  }, [sess?.session_id, started, session.user_id]);

  // audio-reactive analyser + RAF visual loop
  useEffect(() => {
    if (!started) return;
    let raf;
    const setup = () => {
      try {
        const a = audioRef.current;
        if (a && !audioCtxRef.current) {
          const AC = window.AudioContext || window.webkitAudioContext;
          const ctx = new AC();
          const src = ctx.createMediaElementSource(a);
          const an = ctx.createAnalyser(); an.fftSize = 128;
          src.connect(an); an.connect(ctx.destination);
          audioCtxRef.current = ctx; analyserRef.current = an;
        }
      } catch (e) {}
    };
    setup();
    const freq = new Uint8Array(64);
    const loop = () => {
      const an = analyserRef.current;
      let level = 0;
      const heights = new Array(24).fill(6);
      if (an) {
        an.getByteFrequencyData(freq);
        for (let i = 0; i < 24; i++) { const v = freq[i * 2] / 255; heights[i] = 6 + v * 46; level += v; }
        level = level / 24;
      }
      // idle shimmer when silent
      const t = Date.now() / 240;
      levelRef.current = level;
      if (barsWrap.current) {
        const kids = barsWrap.current.children;
        for (let i = 0; i < kids.length; i++) {
          const base = level > 0.02 ? heights[i] : 6 + Math.abs(Math.sin(t + i * 0.5)) * (effState === "idle" ? 4 : 10);
          kids[i].style.height = base + "px";
        }
      }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [started, effState]);

  const begin = () => {
    if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
    if (audioRef.current) { audioRef.current.play().catch(() => {}); audioRef.current.pause(); }
    setStarted(true);
    setBooting(true);
  };
  const bootDone = (b) => {
    setBooting(false);
    if (b?.text) setReplyText(b.text);
    if (b?.audio_key && audioRef.current) {
      audioRef.current.src = audioUrlForKey(b.audio_key);
      audioRef.current.play().catch(() => {});
      setStatus("speaking");
    }
  };

  const sendBlob = useCallback(async (blob) => {
    if (!sess?.session_id || !blob || blob.size < 1200) { setStatus("idle"); return; }
    setStatus("thinking"); busyRef.current = true;
    try {
      const r = await nexusTalk(sess.session_id, session.user_id, blob);
      setUserText(r.user_text || ""); setReplyText(r.reply_text || "");
      if (r.mood) setMood(r.mood);
      if (r.audio_key && audioRef.current) {
        lastTurn.current = r.turn_id || lastTurn.current + 1;
        audioRef.current.src = audioUrlForKey(r.audio_key);
        audioRef.current.play().catch(() => {});
        setStatus("speaking");
      }
    } catch (e) { toast.error("Nexus could not answer"); setStatus("idle"); }
    finally { busyRef.current = false; }
  }, [sess?.session_id, session.user_id]);

  const startRec = useCallback(async () => {
    if (recording || busyRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); setRecording(false); sendBlob(new Blob(chunks, { type: "audio/webm" })); };
      mediaRef.current = mr; mr.start(); setRecording(true); setStatus("listening");
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC(); const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser(); an.fftSize = 512; src.connect(an);
      const data = new Uint8Array(an.frequencyBinCount); const startedAt = Date.now(); let quietSince = null;
      const tick = () => {
        if (mediaRef.current?.state !== "recording") { ctx.close().catch(() => {}); return; }
        an.getByteTimeDomainData(data);
        let sum = 0; for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length); const elapsed = Date.now() - startedAt;
        if (rms < 0.02) { if (!quietSince) quietSince = Date.now(); } else { quietSince = null; }
        const quietFor = quietSince ? Date.now() - quietSince : 0;
        if ((elapsed > 900 && quietFor > 1100) || elapsed > 12000) { try { mediaRef.current.stop(); } catch (e) {} ctx.close().catch(() => {}); return; }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch (e) { toast.error("Allow microphone access on this screen"); setRecording(false); }
  }, [recording, sendBlob]);

  // Wake word "Hey Nexus" on the TV itself
  useEffect(() => {
    if (!wake || !started) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.message("Hands-free needs Chrome/Edge — use Tap to speak"); setWake(false); return; }
    const recog = new SR(); recog.continuous = true; recog.interimResults = true; recog.lang = "en-US";
    recog.onresult = (ev) => {
      if (busyRef.current || recording) return;
      const text = Array.from(ev.results).map((r) => r[0].transcript).join(" ").toLowerCase();
      if (text.includes("nexus")) { try { recog.stop(); } catch (e) {} startRec(); }
    };
    recog.onend = () => { if (wake && !busyRef.current && !recording) { try { recog.start(); } catch (e) {} } };
    recog.onerror = () => {};
    recogRef.current = recog; try { recog.start(); } catch (e) {}
    return () => { try { recog.stop(); } catch (e) {} };
  }, [wake, started, recording, startRec]);


  const onEnded = async () => {
    if (sess?.session_id) { try { await nexusIdle(sess.session_id); } catch (e) {} }
    setStatus("idle"); setUserText(""); setReplyText(""); setMood("neutral");
  };

  return (
    <div className={`relative w-full ${screen ? "h-screen" : "h-[calc(100vh-56px)]"} overflow-hidden bg-[#030712] text-[#F3F4F6] nx-display nx-scanlines nx-vignette select-none`} data-testid="nexus-tv-hud">
      <Particles color={color} />
      <div className="absolute inset-0 nx-grid opacity-60" />

      {!started ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-8 z-30">
          <div className="nx-orbitron text-5xl font-black tracking-[0.3em]" style={{ color, textShadow: `0 0 30px ${color}` }}>NEXUS</div>
          <button onClick={begin} autoFocus data-testid="nexus-tv-start"
            className="nx-glass px-10 py-5 rounded-2xl text-xl font-bold tracking-widest uppercase flex items-center gap-3 hover:scale-105 transition-transform"
            style={{ color, boxShadow: `0 0 40px ${color}44` }}>
            <Power className="w-6 h-6" /> Initialize
          </button>
          <p className="nx-mono text-cyan-300/50 text-sm">tap once to enable audio · then type or speak commands from your phone app</p>
        </div>
      ) : screen === "a" ? (
        <div className="absolute inset-0 z-10 grid grid-rows-[88px_minmax(0,1fr)]" data-testid="tv-a">
          <TvEyeAlert userId={session.user_id} wallOnly />
          <header className="grid grid-cols-[1fr_auto_1fr] items-center gap-6 px-8 border-b border-cyan-300/10" data-testid="tv-a-header">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-[76px] h-[76px] rounded-full overflow-hidden pointer-events-none shrink-0" data-testid="nexus-arc-reactor-core">
                <NexusFace3D colorHex={color} state={effState} levelRef={levelRef} variant={variant} gazeRef={gazeRef} mood={eyeAlarm ? "danger" : mood !== "neutral" ? mood : eyeWho.length ? "good" : faceMood} />
              </div>
              <div className="min-w-0"><p className="nx-orbitron font-black tracking-widest text-xl leading-none">A V NEXUS</p><p className="nx-mono text-[10px] tracking-[0.3em] text-cyan-300/50">TV A · PRESENCE · {s.sub}</p></div>
            </div>
            <div className="nx-glass px-6 py-2 rounded-full flex items-center gap-3 whitespace-nowrap" data-testid="nexus-state-badge">
              <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />
              <span className="nx-orbitron font-bold tracking-[0.25em] text-sm" style={{ color }}>{s.label}</span>
            </div>
            <div className="justify-self-end" data-testid="tv-live-clock"><LiveClock big /></div>
          </header>
          <main className="min-h-0 px-8 py-5 relative" data-testid="tv-a-channel-feed"><TvChannelFeed /></main>
          {eyeBanner && !["gesture", "business"].includes(eyeBanner.kind) && (
            <div className={`absolute inset-x-0 ${eyeBanner.kind === "business" ? "bottom-20" : "top-24"} z-50 flex justify-center pointer-events-none ${eyeBanner.kind === "intruder" ? "animate-pulse" : ""}`} data-testid={`nexus-eye-banner-${eyeBanner.kind}`}>
              <div className={`rounded-3xl px-12 py-6 text-center border-4 max-w-[90vw] ${["intruder", "health"].includes(eyeBanner.kind) ? "bg-rose-950/95 border-rose-500 text-rose-100" : "bg-emerald-950/95 border-emerald-400 text-emerald-100"}`}>
                <p className="nx-orbitron text-[clamp(2rem,4vw,4rem)] font-black tracking-widest">{eyeBanner.kind === "intruder" ? "INTRUDER" : eyeBanner.kind === "health" ? "HEALTH CHECK" : eyeBanner.kind === "gesture" ? "NEXUS" : eyeBanner.name === "Business" ? "BUSINESS BRIEF" : `WELCOME, ${eyeBanner.name.toUpperCase()}`}</p>
                <p className="mt-3 text-[clamp(1rem,1.6vw,1.5rem)] line-clamp-3">{eyeBanner.text}</p>
              </div>
            </div>
          )}
          {eyeAlarm && <div className="absolute inset-0 z-[45] pointer-events-none border-[12px] border-rose-600 animate-pulse" data-testid="nexus-eye-alarm-frame" />}
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-8 py-5 bg-gradient-to-b from-[#030712]/90 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg nx-glass flex items-center justify-center" style={{ color: color }}><Cpu className="w-5 h-5" /></div>
              <div>
                <p className="nx-orbitron font-black tracking-widest text-lg leading-none">A V NEXUS</p>
                <p className="nx-mono text-[10px] tracking-[0.3em] text-cyan-300/50">NEURAL VOICE OS · v4.2</p>
              </div>
            </div>
            <div className="nx-glass px-6 py-2 rounded-full flex items-center gap-3" data-testid="nexus-state-badge">
              <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />
              <span className="nx-orbitron font-bold tracking-[0.25em] text-sm" style={{ color: color }}>{s.label}</span>
            </div>
            <div className="text-right nx-mono">
              <p className="text-[10px] tracking-[0.3em] text-cyan-300/50">STATUS</p>
              <p className="text-xl font-bold tracking-[0.2em]" style={{ color }} data-testid="nexus-tv-status">{s.sub}</p>
              <p className="text-[10px] text-cyan-300/40">{clock} CT · READY</p>
            </div>
          </div>

          {/* Left telemetry panel — context radar */}
          {wall && (
            <div className="absolute right-6 top-24 bottom-16 left-[35%] z-20" data-testid="nexus-wall-layout">
              <NexusScenes userId={session.user_id} onPnl={onPnl} onVariant={setCoreVariant} />
            </div>
          )}
          {wall && <WallTicker userId={session.user_id} />}
          <div className={`absolute left-8 top-28 w-72 z-20 ${wall ? "hidden" : "hidden lg:flex"} flex-col gap-3`} data-testid="nexus-context-radar-panel">
            <p className="nx-mono text-[10px] tracking-[0.3em] text-cyan-300/60 flex items-center gap-2"><Activity className="w-3 h-3" /> CONTEXT RADAR</p>
            {[
              { icon: FileText, k: "SCANNED DOCS", v: ctxData.docs },
              { icon: Clock, k: "ACTIVE DEADLINES", v: ctxData.deadlines },
              { icon: MessageSquareX, k: "WHATSAPP FLAGS", v: ctxData.wa },
            ].map((row, i) => (
              <div key={i} className="nx-glass rounded-xl p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-4 h-4 nx-bracket-tl" />
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-cyan-100/80 text-sm font-semibold tracking-wide"><row.icon className="w-4 h-4" style={{ color: color }} /> {row.k}</span>
                  <span className="nx-orbitron text-2xl font-black" style={{ color: color }}>{String(row.v).padStart(2, "0")}</span>
                </div>
              </div>
            ))}
            <div className="nx-glass rounded-xl p-4 flex items-center gap-2 text-xs nx-mono text-cyan-300/70">
              <ShieldCheck className="w-4 h-4" style={{ color: color }} /> SECURE SESSION · MASTER
            </div>
          </div>

          {/* Right telemetry panel — signal / spectrum */}
          <div className={`absolute right-8 top-28 w-72 z-20 ${wall ? "hidden" : "hidden lg:flex"} flex-col gap-3`} data-testid="nexus-telemetry-panel">
            <p className="nx-mono text-[10px] tracking-[0.3em] text-cyan-300/60 flex items-center gap-2 justify-end">VOICE SIGNAL <Radio className="w-3 h-3" /></p>
            <div className="nx-glass rounded-xl p-4">
              <div ref={barsWrap} className="flex items-end justify-between gap-[3px] h-14">
                {Array.from({ length: 24 }).map((_, i) => (
                  <div key={i} style={{ height: 6, background: color, width: 5, borderRadius: 2, boxShadow: `0 0 8px ${color}88`, transition: "height 0.06s linear" }} />
                ))}
              </div>
              <p className="nx-mono text-[10px] text-cyan-300/50 mt-2 tracking-widest">SPECTRAL ANALYZER · 48kHz</p>
            </div>
            <div className="nx-glass rounded-xl p-4 space-y-2 nx-mono text-xs">
              <div className="flex justify-between text-cyan-300/70"><span className="flex items-center gap-1"><Zap className="w-3 h-3" /> ENGINE</span><span style={{ color: color }}>CLAUDE · WHISPER</span></div>
              <div className="flex justify-between text-cyan-300/70"><span className="flex items-center gap-1"><Radio className="w-3 h-3" /> VOICE</span><span style={{ color: color }}>ELEVENLABS</span></div>
              <div className="flex justify-between text-cyan-300/70"><span className="flex items-center gap-1"><Wifi className="w-3 h-3" /> LINK</span><span style={{ color: "#10B981" }}>ONLINE</span></div>
            </div>
          </div>

          {/* Center 3D holo-stage — living neural face / brain that talks */}
          <div className={`absolute z-10 pointer-events-none ${wall ? "left-0 top-20 bottom-12 w-[34%]" : "inset-0"}`} data-testid="nexus-arc-reactor-core">
            <NexusFace3D colorHex={color} state={effState} levelRef={levelRef} variant={variant} gazeRef={gazeRef} mood={eyeAlarm ? "danger" : mood !== "neutral" ? mood : eyeWho.length ? "good" : faceMood} />
            <div className="absolute inset-0 flex items-center justify-center">
              {effState === "listening" && [0, 1, 2].map((i) => (
                <div key={i} className={`nx-ripple ${i === 1 ? "d2" : i === 2 ? "d3" : ""}`} style={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", border: `2px solid ${color}` }} />
              ))}
            </div>
          </div>

          {/* Optional this-screen mic (advanced) — no on-screen command text */}
          <div className={`absolute ${wall ? "top-36 left-6" : "bottom-28 left-1/2 -translate-x-1/2"} z-30 flex flex-col items-center gap-3`}>
            <button onClick={() => setWake((w) => !w)} data-testid="nexus-tv-wake"
              className={`nx-mono text-[10px] tracking-widest flex items-center gap-1 px-3 py-1 rounded-full border transition-colors ${wake ? "text-[#10B981] border-[#10B981]" : "text-cyan-200/30 border-cyan-200/10"}`}>
              {status === "thinking" ? <Loader2 className="w-3 h-3 animate-spin" /> : wake ? <Mic className="w-3 h-3" /> : <Radio className="w-3 h-3" />}
              {wake ? "THIS-SCREEN MIC ON · SAY “NEXUS”" : "use this screen's mic (advanced)"}
            </button>
          </div>

          {/* Core appearance toggle — Face (lip-syncs) vs Brain */}
          <div className={`absolute ${wall ? "top-24 left-6" : "bottom-8 left-8"} z-30 flex items-center gap-1 nx-glass rounded-full p-1`} data-testid="nexus-core-variant">
            <span className={`px-2 nx-mono text-[10px] tracking-widest ${eyeAlarm ? "text-rose-400" : eyeOn ? "text-emerald-300" : "text-cyan-200/30"}`} data-testid="nexus-eye-indicator">
              {eyeAlarm ? "● STRANGER" : eyeWho.length ? `● ${eyeWho.join(", ").toUpperCase()}` : eyeOn ? "● WATCHING" : "○ EYE OFF"}
            </span>
          </div>

          {/* Captions */}
          <div className={`absolute z-30 ${wall ? "bottom-16 left-6 w-[32%]" : "bottom-6 left-1/2 -translate-x-1/2 w-11/12 max-w-4xl"}`} data-testid="nexus-captions-container">
            {(userText || replyText) ? (
              <div className="nx-glass rounded-2xl p-6" style={{ boxShadow: `0 0 50px ${color}22` }}>
                {userText && <p className="nx-mono text-cyan-300/70 text-sm mb-2" data-testid="nexus-user-query-text">▸ {userText}</p>}
                {replyText && <p key={replyText} className={`${wall ? "text-xl line-clamp-4" : "text-2xl lg:text-3xl"} font-semibold leading-snug nx-fade-up`} data-testid="nexus-assistant-reply-text">{replyText}</p>}
              </div>
            ) : (
              <p className="text-center nx-mono text-cyan-300/40 tracking-widest text-sm">{s.sub}</p>
            )}
          </div>

          {eyeBanner && (
            <div className={`absolute inset-x-0 ${eyeBanner.kind === "business" ? "bottom-20" : "top-24"} z-50 flex justify-center pointer-events-none ${eyeBanner.kind === "intruder" ? "animate-pulse" : ""}`} data-testid={`nexus-eye-banner-${eyeBanner.kind}`}>
              <div className={`rounded-3xl px-12 py-6 text-center border-4 ${["intruder", "health"].includes(eyeBanner.kind) ? "bg-rose-950/90 border-rose-500 text-rose-100" : "bg-emerald-950/90 border-emerald-400 text-emerald-100"}`}>
                <p className="nx-orbitron text-6xl font-black tracking-widest">{eyeBanner.kind === "intruder" ? "INTRUDER" : eyeBanner.kind === "health" ? "HEALTH CHECK" : eyeBanner.kind === "gesture" ? "NEXUS" : eyeBanner.name === "Business" ? "BUSINESS BRIEF" : `WELCOME, ${eyeBanner.name.toUpperCase()}`}</p>
                <p className={`mt-3 max-w-5xl ${eyeBanner.kind === "business" ? "text-xl" : "text-2xl"}`}>{eyeBanner.text}</p>
              </div>
            </div>
          )}
          {eyeAlarm && <div className="absolute inset-0 z-[45] pointer-events-none border-[12px] border-rose-600 animate-pulse" data-testid="nexus-eye-alarm-frame" />}

          <div className="fixed bottom-14 right-6 z-[60] pointer-events-none rounded-2xl px-4 py-2 bg-[#030712]/85 border border-cyan-300/25 backdrop-blur-md" data-testid="tv-live-clock"><LiveClock big /></div>

          {/* Pulsating brand */}
          <div className={`absolute ${wall ? "hidden" : "bottom-1 left-1/2 -translate-x-1/2"} z-40 pointer-events-none`} data-testid="nexus-brand">
            <span className="nx-orbitron font-black tracking-[0.45em] text-sm nx-av-pulse" style={{ color }}>A V TECHNOLOGIES</span>
          </div>
          <style>{`@keyframes nxAvPulse {
            0%,100% { transform: scale(1) translateX(0); opacity:.7; text-shadow:0 0 6px currentColor; }
            25% { transform: scale(1.07) translateX(-1.5px); opacity:.9; }
            50% { transform: scale(1.12) translateX(1.5px); opacity:1; text-shadow:0 0 24px currentColor, 0 0 40px currentColor; }
            75% { transform: scale(1.07) translateX(-1.5px); opacity:.9; }
          } .nx-av-pulse { display:inline-block; animation: nxAvPulse 1.5s ease-in-out infinite; }`}</style>

          {/* Dev state preview switcher */}
          <div className={`absolute bottom-3 right-4 z-40 gap-1 opacity-40 hover:opacity-100 transition-opacity ${wall ? "hidden" : "flex"}`}>
            {["idle", "listening", "thinking", "speaking"].map((k) => (
              <button key={k} onClick={() => setPreview(preview === k ? null : k)} data-testid={`nexus-state-btn-${k}`}
                className="nx-mono text-[9px] uppercase px-2 py-1 rounded border tracking-widest"
                style={{ borderColor: preview === k ? STATES[k].color : "#ffffff22", color: preview === k ? STATES[k].color : "#7DD3FC88" }}>
                {k}
              </button>
            ))}
          </div>
        </>
      )}
      {booting && <NexusBoot userId={session.user_id} onDone={bootDone} />}
      <audio ref={audioRef} onEnded={onEnded} data-testid="nexus-tv-audio" crossOrigin="anonymous" />
    </div>
  );
}
